// SAB item 5: observe-only cache of the browser GPS position.
//
// Locance's HTML5 collector gathers a fix from our top document, but its result to us is
// only a receipt (no coordinates, confirmed by the coord probe). To feed the Soap
// device_ping with lat/long WITHOUT a second prompt, we wrap the geolocation methods
// observe-only: when the collector fixes a position, we cache it, then call through
// untouched. Same underlying call, no extra prompt.
//
// The collector uses navigator.geolocation.WATCHPOSITION (verified against the live
// bl1.locance.net/v4/html5 script: its dictionary carries watchPosition / watchDuration /
// clearWatch / WatchTimeout / stopWatch — a watch lifecycle, not getCurrentPosition). So we
// MUST wrap watchPosition to observe the fixes; getCurrentPosition is wrapped too for
// completeness. This mirrors nativeGeolocationShim, which already shims both.
//
// Web first. On native, navigator.geolocation is the SAB-572 Capacitor shim; wrapping it
// there is deferred, so install() no-ops on native for now.

import { Capacitor } from '@capacitor/core';

interface CachedPosition { latitude: number; longitude: number; ts: number; }

let cached: CachedPosition | null = null;
let installed = false;

/** Latest cached fix if it is at most maxAgeMs old, else null. Never prompts. */
export function getCachedPosition(maxAgeMs: number): { latitude: number; longitude: number } | null {
  if (!cached) return null;
  if (Date.now() - cached.ts > maxAgeMs) return null;
  return { latitude: cached.latitude, longitude: cached.longitude };
}

/** Cache a fix if it carries finite coordinates. Never throws (must not break Locance). */
function observe(pos: GeolocationPosition | null | undefined): void {
  try {
    const c = pos?.coords;
    if (c && Number.isFinite(c.latitude) && Number.isFinite(c.longitude)) {
      cached = { latitude: c.latitude, longitude: c.longitude, ts: Date.now() };
    }
  } catch {
    /* caching must never break the caller (Locance) */
  }
}

/**
 * Wrap navigator.geolocation.getCurrentPosition AND watchPosition observe-only so every
 * successful fix Locance collects is cached. watchPosition is the one the collector actually
 * uses (see file header); getCurrentPosition is wrapped for completeness. Both call through
 * to the real method untouched — no extra call, no extra prompt. Idempotent; web only for
 * now. Installed from main.tsx before render, so it is in place before the Locance SDK loads.
 */
export function installGeoPositionCache(): void {
  if (installed) return;
  if (Capacitor.isNativePlatform()) return; // native shim path deferred (web first)
  if (typeof navigator === 'undefined' || !navigator.geolocation) return;
  const geo = navigator.geolocation;

  if (typeof geo.getCurrentPosition === 'function') {
    const originalGet = geo.getCurrentPosition.bind(geo);
    (geo as unknown as { getCurrentPosition: Geolocation['getCurrentPosition'] }).getCurrentPosition = (
      success: PositionCallback,
      error?: PositionErrorCallback | null,
      options?: PositionOptions,
    ): void => {
      originalGet((pos) => { observe(pos); success(pos); }, error, options);
    };
  }

  // The collector's real gather path: a watch that streams fixes until clearWatch. Cache
  // each fix, pass through untouched, and return the real watch id so clearWatch still works.
  if (typeof geo.watchPosition === 'function') {
    const originalWatch = geo.watchPosition.bind(geo);
    (geo as unknown as { watchPosition: Geolocation['watchPosition'] }).watchPosition = (
      success: PositionCallback,
      error?: PositionErrorCallback | null,
      options?: PositionOptions,
    ): number => originalWatch((pos) => { observe(pos); success(pos); }, error, options);
  }

  installed = true;
}
